import { api_fetch } from "@/api/client.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Form, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form.tsx"
import { Input } from "@/components/ui/input.tsx"
import { SearchableSelect } from "@/components/searchable_select.tsx"
import { mergeCategoryOptions, useOrgCategoryNames } from "@/deduction/category_options.ts"
import { useAsyncEffect } from "@/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useState } from "preact/hooks"
import { useForm } from "react-hook-form"
import * as z from "zod"

export const CATEGORY_FORM_SCHEMA = z.object({
  category: z.string().min(1, { message: "Please select a category" }),
  description: z.string().min(1, { message: "Please select a description" }),
  custom_description: z.string().optional(),
})

interface CategoryFormProps {
  onSubmit: (values: any) => void
  deductionId: string
  initialValue?: string
  initialDescription?: string
}

const DEFAULT_DESCRIPTIONS = ["MCB", "Short ship", "Price support - on shelf", "Placement", "Free fill"]

export function categoryFormDefaultValues(initialValue?: string, initialDescription?: string) {
  return {
    category: initialValue ?? "",
    description: initialDescription ?? "",
    custom_description: "",
  }
}

export function CategoryForm({ onSubmit, deductionId, initialValue, initialDescription }: CategoryFormProps) {
  const defaults = categoryFormDefaultValues(initialValue, initialDescription)
  const form = useForm<z.infer<typeof CATEGORY_FORM_SCHEMA>>({
    resolver: zodResolver(CATEGORY_FORM_SCHEMA),
    defaultValues: defaults,
  })

  const [descriptionMap, setDescriptionMap] = useState<Record<string, string[]>>({})
  const [apiCategories, setApiCategories] = useState<string[]>([])
  const [selectedDescription, setSelectedDescription] = useState<string>(defaults.description)
  const orgCategoryNames = useOrgCategoryNames()

  const handleSelectDescription = (description: string) => {
    setSelectedDescription(description)
    form.setValue("description", description)
  }

  const handleSelectCategory = (category: string) => {
    const previousCategory = form.getValues("category")
    form.setValue("category", category)

    // reset description so that we only allow selecting a category - description pair
    if (category !== previousCategory) {
      form.setValue("description", "")
      setSelectedDescription("")
    }
  }

  function handleSubmit(values: z.infer<typeof CATEGORY_FORM_SCHEMA>) {
    const payload: z.infer<typeof CATEGORY_FORM_SCHEMA> = {
      category: values.category,
      description: values.description,
    }
    if (values.description.toLowerCase() === "other" && values.custom_description?.trim()) {
      payload.custom_description = values.custom_description.trim()
    }
    onSubmit(payload)
  }

  useEffect(() => {
    if (!initialDescription) {
      return
    }
    form.setValue("description", initialDescription)
    setSelectedDescription(initialDescription)
  }, [initialDescription])

  useAsyncEffect(async () => {
    const res = await api_fetch<Record<string, string[]>>(`/deduction/${deductionId}/category_descriptions`)

    if (res.ok) {
      let data = res.value.data
      setApiCategories(Object.keys(data).filter(category => category !== ""))
      setDescriptionMap(data)
    }
  }, [])

  const selectedCategory = form.watch("category")

  const getMergedCategories = () => {
    // `apiCategories` (this deduction's source, suppression-filtered) and `initialValue` are kept
    // as extras so the current selection always shows; `orgCategoryNames` drives the rest.
    const extras = [...apiCategories]
    if (initialValue) extras.push(initialValue)
    return mergeCategoryOptions(orgCategoryNames, extras)
  }

  const getMergedDescriptions = () => {
    const categoryDescriptions = selectedCategory in descriptionMap ? descriptionMap[selectedCategory] : []
    const uniqueDescriptions = new Set([...DEFAULT_DESCRIPTIONS, ...categoryDescriptions])
    if (initialDescription) {
      uniqueDescriptions.add(initialDescription)
    }
    uniqueDescriptions.delete("Other")
    return ["Other", ...Array.from(uniqueDescriptions).sort()]
  }

  return (
    <Form {...form}>
      <form
        onSubmit={e => {
          form.handleSubmit(handleSubmit)(e)
        }}
        className="max-h-[calc(var(--radix-popover-content-available-height,90dvh)-0.5rem)] w-full max-w-md space-y-6 overflow-y-auto overscroll-y-contain pr-0.5">
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Category</FormLabel>
              <SearchableSelect
                insideModal
                value={field.value}
                options={getMergedCategories().map(c => ({ value: c, label: c }))}
                onValueChange={handleSelectCategory}
                placeholder="Select a category..."
                searchPlaceholder="Search category..."
                emptyMessage="No categories found."
                contentClassName="w-[var(--radix-popover-trigger-width)]"
                listClassName="max-h-[26rem]"
              />
              <FormMessage />
            </FormItem>
          )}
        />

        {selectedCategory && (
          <FormField
            control={form.control}
            name="description"
            render={() => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <SearchableSelect
                  insideModal
                  value={selectedDescription}
                  options={getMergedDescriptions().map(d => ({ value: d, label: d }))}
                  onValueChange={handleSelectDescription}
                  placeholder="Select description..."
                  searchPlaceholder="Search description..."
                  emptyMessage="No descriptions found."
                  contentClassName="w-[var(--radix-popover-trigger-width)]"
                  listClassName="max-h-[26rem]"
                />
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {selectedDescription?.toLowerCase() === "other" && (
          <FormField
            control={form.control}
            name="custom_description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Custom description (optional)</FormLabel>
                <Input
                  {...field}
                  // @ts-ignore
                  type="text"
                  className="input"
                  placeholder="Enter a custom description"
                />
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <Button type="submit" variant="tertiary" className="w-full">
          Update
        </Button>
      </form>
    </Form>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("categoryFormDefaultValues", () => {
    it("pre-fills category and description from initial props", () => {
      const values = categoryFormDefaultValues("Invoice Discrepancy", "Short ship")
      expect(values.category).toBe("Invoice Discrepancy")
      expect(values.description).toBe("Short ship")
    })
  })
}
